/**
 * pes-concierge.js — High-intent concierge triggers for PES Supply (v1, 2026-09-20)
 *
 * OWNER DIRECTIVE: every visitor gets identified, concierged, and HANDLED.
 * This asset is the storefront concierge layer: it watches for high-intent
 * behavior and offers a relevant specialist conversation BEFORE the visitor
 * bounces. All handling routes into the Intercom messenger (app lt1fbeyf)
 * via the PowerLine/showNewMessage prefill pattern.
 *
 * HARD RULES (inherited from pes-intercom-engage-v2.js — the 2026-10
 * "fin/eol fix" removed ALL auto-opens after stale composer drafts):
 *   1. NEVER auto-open the messenger. The chip appears; Intercom('showNewMessage')
 *      runs ONLY on an explicit visitor click of this file's CTA.
 *   2. Nudges are small dismissible chips anchored above the chat bubble —
 *      not modals, not overlays.
 *   3. Max ONE concierge chip per session per trigger; dismissal persists 24h;
 *      only one chip visible at a time (yields to pes-intercom-engage-v2 nudges).
 *   4. Freight-quote-first: when the freight form is on the page, the CTA
 *      scrolls to the form instead of opening chat.
 *
 * TRIGGERS:
 *   A. Heavy-goods PDP dwell > 45s (freight-flagged DOM marker OR product
 *      price >= $1,000) -> "Need a freight quote on this [product]?"
 *   B. >= 3 PDPs viewed in the same category this session ->
 *      "Comparing [category]? We'll spec and price the whole package."
 *   C. Quote page (/pages/quote, /pages/request-a-quote) dwell > 20s ->
 *      "Want a specialist to build this quote with you?"
 *
 * MEASUREMENT (every chip): Intercom trackEvent (pes_concierge_shown /
 * pes_concierge_engaged / pes_concierge_dismissed), dataLayer push,
 * Clarity event, guarded Klaviyo onsite track, and an Intercom contact
 * attribute update (last_concierge_trigger — attribute created via API
 * 2026-09-20). Anonymous visitors are therefore not silent: the event lands
 * on their lead record even if they never chat.
 *
 * ADDITIVE: does not modify pes-intercom-unified.js, pes-powerline-chat.js,
 * or pes-intercom-engage-v2.js. If Intercom is blocked/absent, CTAs degrade
 * to the freight form or /pages/quote.
 *
 * VOICE: wholesale / trade. "169 authorized brands", "trade pricing",
 * "50,000+ SKUs", "7-10 business days delivery nationwide".
 * NEVER free shipping, NEVER same-day shipping, NEVER "112,000+ products".
 *
 * INSTALL: one line in layout/theme.liquid AFTER pes-intercom-unified.js:
 *   <script src="{{ 'pes-concierge.js' | asset_url }}" defer="defer"></script>
 *
 * 2026-09-20 · pushed to prod theme 142727512198 (backup-first via
 * shopify_safe.py safe_put_theme_asset).
 */
(function () {
  'use strict';

  if (window.__pesConcierge) return; /* single instance */
  window.__pesConcierge = true;

  var w = window, d = document;

  /* ---------------- tunables ---------------- */
  var PDP_DWELL_MS = 45000;          /* trigger A: 45s on heavy-goods PDP */
  var QUOTE_DWELL_MS = 20000;        /* trigger C: 20s on a quote page */
  var CATEGORY_PDP_COUNT = 3;        /* trigger B: >=3 PDPs, same category, session */
  var HEAVY_PRICE_CENTS = 100000;    /* >= $1,000 counts as heavy-goods */
  var DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
  var LS_DISMISS = 'pes_concierge_dismissed';
  var SS_SHOWN = 'pes_concierge_shown';
  var SS_PDPS = 'pes_concierge_pdps';
  var PES_GREEN = '#11B67B';
  var PES_INK = '#242833';
  var SALES_PHONE = '(888) 876-0007';
  var SALES_PHONE_TEL = '+18888760007';
  var QUOTE_URL = '/pages/quote';

  /* ---------------- tiny utils ---------------- */
  function lsGet(k) { try { return JSON.parse(localStorage.getItem(k) || '{}'); } catch (e) { return {}; } }
  function lsSet(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  function ssArr(k) { try { return JSON.parse(sessionStorage.getItem(k) || '[]'); } catch (e) { return []; } }
  function ssPush(k, val) {
    try {
      var a = ssArr(k);
      a.push(val);
      sessionStorage.setItem(k, JSON.stringify(a));
    } catch (e) {}
  }
  function dismissed(key) {
    var m = lsGet(LS_DISMISS);
    return m[key] && (Date.now() - m[key] < DISMISS_TTL_MS);
  }
  function markDismissed(key) { var m = lsGet(LS_DISMISS); m[key] = Date.now(); lsSet(LS_DISMISS, m); }
  function shownThisSession(key) { return ssArr(SS_SHOWN).indexOf(key) !== -1; }

  function path() { return w.location.pathname; }
  function isProductPage() { return path().indexOf('/products/') !== -1; }
  function isQuotePage() {
    return path() === '/pages/quote' || path().indexOf('/pages/request-a-quote') !== -1;
  }

  /* ---------------- measurement (all guarded, all additive) ---------------- */
  function track(trigger, action, meta) {
    var payload = meta || {};
    payload.trigger = trigger;
    payload.page_url = w.location.href;
    try {
      if (typeof w.Intercom === 'function') {
        w.Intercom('trackEvent', 'pes_concierge_' + action, payload);
      }
    } catch (e) {}
    try {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({
        event: 'pes_concierge_' + action,
        concierge_trigger: trigger,
        page_url: payload.page_url
      });
    } catch (e) {}
    try {
      if (typeof w.clarity === 'function') w.clarity('event', 'pes_concierge_' + action + '_' + trigger);
    } catch (e) {}
    try {
      if (w.klaviyo && typeof w.klaviyo.push === 'function') {
        w.klaviyo.push(['track', 'PES Concierge ' + action.charAt(0).toUpperCase() + action.slice(1), payload]);
      } else if (w._learnq && typeof w._learnq.push === 'function') {
        w._learnq.push(['track', 'PES Concierge ' + action.charAt(0).toUpperCase() + action.slice(1), payload]);
      }
    } catch (e) {}
    /* Stamp the contact so the trigger is visible on the Intercom record
       even when the visitor never engages. Attribute created via API
       2026-09-20 (top-level key = custom attribute, same pattern as
       unified.js rb2b_* updates). */
    if (action === 'shown' || action === 'engaged') {
      try {
        if (typeof w.Intercom === 'function') {
          w.Intercom('update', { last_concierge_trigger: trigger });
        }
      } catch (e) {}
    }
  }

  /* ---------------- page context helpers ---------------- */
  function productInfo() {
    var info = { title: '', priceCents: 0, category: '' };
    var ld = d.querySelectorAll('script[type="application/ld+json"]');
    var i, data, prod;
    for (i = 0; i < ld.length; i++) {
      try {
        data = JSON.parse(ld[i].textContent);
        prod = Array.isArray(data) ? data[0] : data;
        if (prod && prod['@type'] === 'Product') {
          info.title = prod.name || '';
          info.category = prod.category || '';
          if (prod.offers) {
            var offers = Array.isArray(prod.offers) ? prod.offers : [prod.offers];
            for (var k = 0; k < offers.length; k++) {
              var p = parseFloat(offers[k] && (offers[k].price || (offers[k].priceSpecification && offers[k].priceSpecification.price)));
              if (p && p * 100 > info.priceCents) info.priceCents = Math.round(p * 100);
            }
          }
          break;
        }
      } catch (e) {}
    }
    if (!info.title) {
      var og = d.querySelector('meta[property="og:title"]');
      if (og) info.title = og.getAttribute('content') || '';
    }
    if (!info.category) {
      /* breadcrumb fallback (same approach as unified.js) */
      for (i = 0; i < ld.length; i++) {
        try {
          data = JSON.parse(ld[i].textContent);
          var bc = Array.isArray(data) ? data[0] : data;
          if (bc && bc['@type'] === 'BreadcrumbList' && bc.itemListElement && bc.itemListElement.length > 2) {
            var crumb = bc.itemListElement[bc.itemListElement.length - 2];
            var cname = (crumb && (crumb.name || (crumb.item && crumb.item.name))) || '';
            if (cname && cname.toLowerCase() !== 'home') { info.category = cname; break; }
          }
        } catch (e) {}
      }
    }
    return info;
  }

  function freightRoot() {
    return d.querySelector('[data-pes-fq-context], #PesFreightQuote, #PesFreightQuotePdp, .pes-fq, #PesShippingEstimator, .pes-est');
  }

  function scrollToFreightForm(root) {
    var panel = root.querySelector('[data-pes-fq-panel][hidden]');
    var toggle = root.querySelector('[data-pes-fq-toggle]');
    if (panel && toggle) { try { toggle.click(); } catch (e) {} }
    try { root.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { root.scrollIntoView(); }
  }

  /* Explicit-click chat open — the ONLY place this file opens the messenger. */
  function openChatWith(prefill) {
    if (w.Intercom && typeof w.Intercom === 'function') {
      try { w.Intercom('showNewMessage', prefill); return; } catch (e) {}
    }
    w.location.href = QUOTE_URL;
  }

  /* ---------------- chip UI (PowerLine palette) ---------------- */
  function injectStyles() {
    if (d.getElementById('pes-concierge-styles')) return;
    var css =
      '.pes-concierge-chip{position:fixed;right:20px;bottom:96px;z-index:2147482900;' +
      'max-width:330px;background:#fff;border:1px solid #dde3ea;border-left:4px solid ' + PES_GREEN + ';' +
      'border-radius:10px;box-shadow:0 10px 28px rgba(36,40,51,.18);padding:14px 16px 12px;' +
      'font-family:Poppins,Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;' +
      'color:' + PES_INK + ';line-height:1.45}' +
      '.pes-concierge-chip .pes-cc-kicker{display:block;font-size:10.5px;font-weight:700;letter-spacing:.08em;' +
      'text-transform:uppercase;color:' + PES_GREEN + ';margin-bottom:4px}' +
      '.pes-concierge-chip p{margin:0 0 10px;font-size:13.5px}' +
      '.pes-concierge-chip .pes-cc-cta{display:inline-block;background:' + PES_GREEN + ';color:#fff;' +
      'border:none;border-radius:6px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;' +
      'font-family:inherit}' +
      '.pes-concierge-chip .pes-cc-cta:hover{background:#0da06c}' +
      '.pes-concierge-chip .pes-cc-dismiss{position:absolute;top:6px;right:8px;background:none;border:none;' +
      'font-size:16px;line-height:1;color:#8a919c;cursor:pointer;padding:4px}' +
      '.pes-concierge-chip .pes-cc-alt{display:block;margin-top:8px;font-size:11.5px;color:#5a6572}' +
      '.pes-concierge-chip .pes-cc-alt a{color:' + PES_GREEN + ';font-weight:600;text-decoration:none}' +
      '@media (max-width:600px){.pes-concierge-chip{right:12px;left:12px;bottom:88px;max-width:none}}';
    var st = d.createElement('style');
    st.id = 'pes-concierge-styles';
    st.textContent = css;
    d.head.appendChild(st);
  }

  /**
   * Show one concierge chip.
   * opts: { key, kicker, text, ctaLabel, onCta, altHtml, meta }
   */
  function showChip(opts) {
    if (!opts || !opts.key) return;
    if (dismissed(opts.key) || shownThisSession(opts.key)) return;
    /* one chip at a time across BOTH nudge systems; engage-v2 freight/cart
       nudges outrank concierge prompts */
    if (d.querySelector('.pes-concierge-chip, .pes-engage-nudge')) return;
    /* don't stack on top of an open PowerLine panel */
    if (d.querySelector('#pes-powerline-panel.pes-pl-open')) return;

    injectStyles();
    var el = d.createElement('div');
    el.className = 'pes-concierge-chip';
    el.setAttribute('role', 'status');
    el.setAttribute('data-pes-concierge', opts.key);

    var kick = d.createElement('span');
    kick.className = 'pes-cc-kicker';
    kick.textContent = opts.kicker || 'PES Concierge';
    el.appendChild(kick);

    var p = d.createElement('p');
    p.textContent = opts.text;
    el.appendChild(p);

    var cta = d.createElement('button');
    cta.type = 'button';
    cta.className = 'pes-cc-cta';
    cta.textContent = opts.ctaLabel;
    cta.addEventListener('click', function () {
      track(opts.key, 'engaged', opts.meta);
      try { opts.onCta(); } catch (e) {}
      remove();
    });
    el.appendChild(cta);

    if (opts.altHtml) {
      var alt = d.createElement('span');
      alt.className = 'pes-cc-alt';
      alt.innerHTML = opts.altHtml;
      el.appendChild(alt);
    }

    var x = d.createElement('button');
    x.type = 'button';
    x.className = 'pes-cc-dismiss';
    x.setAttribute('aria-label', 'Dismiss');
    x.textContent = '\u00D7';
    x.addEventListener('click', function () {
      markDismissed(opts.key);
      track(opts.key, 'dismissed', opts.meta);
      remove();
    });
    el.appendChild(x);

    function remove() { if (el.parentNode) el.parentNode.removeChild(el); }

    d.body.appendChild(el);
    ssPush(SS_SHOWN, opts.key);
    track(opts.key, 'shown', opts.meta);
  }

  /* ---------------- visible-dwell timer ---------------- */
  function afterVisibleDwell(ms, cb) {
    var elapsed = 0;
    var tick = 1000;
    var timer = setInterval(function () {
      if (d.visibilityState === 'visible') {
        elapsed += tick;
        if (elapsed >= ms) { clearInterval(timer); cb(); }
      }
    }, tick);
  }

  /* ---------------- trigger A: heavy-goods PDP dwell ---------------- */
  function armHeavyPdpTrigger() {
    if (!isProductPage()) return;
    var info = productInfo();
    var freight = !!freightRoot();
    var heavy = freight || info.priceCents >= HEAVY_PRICE_CENTS;
    if (!heavy) return;
    afterVisibleDwell(PDP_DWELL_MS, function () {
      var root = freightRoot();
      var what = info.title ? info.title : 'this unit';
      showChip({
        key: 'heavy_pdp_freight',
        kicker: 'Freight desk',
        text: 'Need a freight quote on ' + what + '? Heavy equipment prices out wrong at checkout — we quote exact delivered pricing to your site.',
        ctaLabel: root ? 'Get an exact freight quote' : 'Ask the freight desk',
        onCta: function () {
          if (root) {
            scrollToFreightForm(root);
          } else {
            openChatWith(
              'Hi — I\'m looking at ' + what + ' and need a freight / delivered-price quote.\n\n' +
              'Product: ' + w.location.href + '\n' +
              'Ship-to ZIP: '
            );
          }
        },
        altHtml: 'Replies within 1 business day. Prefer to talk? <a href="tel:' + SALES_PHONE_TEL + '">' + SALES_PHONE + '</a>',
        meta: { product_title: info.title, price_cents: info.priceCents, freight_flagged: freight }
      });
    });
  }

  /* ---------------- trigger B: >=3 PDPs in one category (session) ---------------- */
  function recordAndCheckCategoryDepth() {
    if (!isProductPage()) return;
    var info = productInfo();
    var cat = info.category || 'uncategorized';
    var views = ssArr(SS_PDPS);
    /* record this PDP (dedupe same URL re-view back-to-back) */
    var url = path();
    if (!views.length || views[views.length - 1].url !== url) {
      ssPush(SS_PDPS, { url: url, title: info.title, category: cat });
      views = ssArr(SS_PDPS);
    }
    var inCat = views.filter(function (v) { return v.category === cat; });
    if (inCat.length < CATEGORY_PDP_COUNT) return;
    var titles = inCat.slice(0, 3).map(function (v) { return v.title; }).filter(Boolean);
    showChip({
      key: 'category_depth_' + cat.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40),
      kicker: 'Project desk',
      text: 'Comparing ' + cat.toLowerCase() + ' options? Tell us what you\'re building — we\'ll spec the right unit and price the whole package, freight included.',
      ctaLabel: 'Get a package quote',
      onCta: function () {
        openChatWith(
          'Hi — I\'m comparing ' + cat.toLowerCase() + ' and would like help spec\'ing + pricing the right option.\n\n' +
          'Viewed:\n- ' + (titles.join('\n- ') || '(several)') + '\n' +
          'Current page: ' + w.location.href
        );
      },
      altHtml: 'Trade pricing across 169 authorized brands. <a href="tel:' + SALES_PHONE_TEL + '">' + SALES_PHONE + '</a>',
      meta: { category: cat, pdp_views_in_category: inCat.length, titles: titles }
    });
  }

  /* ---------------- trigger C: quote page specialist offer ---------------- */
  function armQuotePageTrigger() {
    if (!isQuotePage()) return;
    afterVisibleDwell(QUOTE_DWELL_MS, function () {
      showChip({
        key: 'quote_page_specialist',
        kicker: 'Quote specialist',
        text: 'Want a specialist to build this quote with you? Send the list — we\'ll confirm stock, trade pricing, and freight, usually within 1 business day.',
        ctaLabel: 'Hand it to a specialist',
        onCta: function () {
          openChatWith(
            'Hi — I\'m on the quote page and would like a specialist to build my quote.\n\n' +
            'Page: ' + w.location.href + '\n' +
            'What I\'m quoting (rough list is fine): '
          );
        },
        altHtml: 'Net-30 available for qualified business accounts. <a href="tel:' + SALES_PHONE_TEL + '">' + SALES_PHONE + '</a>',
        meta: { quote_page: path() }
      });
    });
  }

  /* ---------------- boot ---------------- */
  function init() {
    recordAndCheckCategoryDepth(); /* immediate — session state, no dwell */
    armHeavyPdpTrigger();
    armQuotePageTrigger();
  }

  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Small public API for other theme code */
  w.PESConcierge = {
    show: showChip,
    openChatWith: openChatWith
  };
})();
